<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br" />

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      <p-tag [severity]="statusSeverity(status())" [value]="statusLabel(status())" />
    </div>
    <div class="buttons">
      @if (canDelete()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="confirmDelete()" />
      }
      @if (canDecide()) {
        <p-button [label]="'ExpenseClaim_Reject' | translateWF: 'Reject'" icon="pi pi-times" severity="danger" variant="outlined" (onClick)="openReject()" />
        <p-button [label]="'ExpenseClaim_Approve' | translateWF: 'Approve'" icon="pi pi-check" severity="success" (onClick)="openApprove()" />
      }
      @if (canSubmit()) {
        <p-button [label]="'ExpenseClaim_Submit' | translateWF: 'Send for approval'" icon="pi pi-send" variant="outlined" (onClick)="submit()" />
      }
      @if (canReview()) {
        <p-button [label]="'ExpenseClaim_SendBack' | translateWF: 'Back to approval'" icon="pi pi-replay" severity="danger" variant="outlined" (onClick)="openSendBack()" />
        <p-button [label]="'ExpenseClaim_Post' | translateWF: 'Post'" icon="pi pi-book" severity="success" [disabled]="!preview()?.isReady" (onClick)="openPost()" />
      }
      @if (canEdit() && isDraft()) {
        @if (canDelete() || canSubmit()) {
          <span class="separator"></span>
        }
        <p-splitbutton [label]="isNew() ? ('ExpenseClaim_Create' | translateWF: 'Create claim') : ('Save' | translateWF: 'Save')" (onClick)="save(false)" [model]="saveMenuItems" />
      }
      <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
    </div>
  </header>

  <div class="content">
    <p-confirmDialog />

    <div class="flex flex-wrap items-center gap-3 mb-5">
      @for (step of steps(); track step.status; let i = $index) {
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold"
            [ngClass]="
              step.current
                ? 'bg-[var(--p-primary-color)] text-white'
                : step.done
                  ? 'bg-[var(--p-primary-100)] text-[var(--p-primary-700)]'
                  : 'bg-[var(--p-surface-200)] text-[var(--p-surface-600)]'
            "
          >
            @if (step.done) {
              <i class="pi pi-check text-xs" aria-hidden="true"></i>
            } @else {
              {{ i + 1 }}
            }
          </span>
          <span [ngClass]="step.current ? 'font-semibold' : 'text-surface-500'">{{ step.label }}</span>
        </div>
        @if (!$last) {
          <i class="pi pi-angle-right text-surface-400" aria-hidden="true"></i>
        }
      }
    </div>

    @if (lastRejection(); as rejection) {
      <p-message severity="warn" styleClass="mb-5"> {{ 'ExpenseClaim_RejectedBy' | translateWF: 'Rejected by' }} {{ rejection.by }}: “{{ rejection.comment }}” </p-message>
    }

    @if (ready()) {
      <p-panel [header]="'Trip_Section_Next' | translateWF: 'What happens next'" [toggleable]="true" [collapsed]="!isNew()" styleClass="mb-5">
        <p-card>
          <ol class="flex flex-col gap-2 list-decimal pl-5 text-sm">
            <li>{{ 'ExpenseClaim_Next_1' | translateWF: 'Add the fund’s or trip’s complete receipts from the pool.' }}</li>
            <li>{{ 'ExpenseClaim_Next_2' | translateWF: 'Send it for approval: the approvers come from the claim policy and the USD total.' }}</li>
            <li>
              {{ 'ExpenseClaim_Next_3' | translateWF: 'Approvers can take a receipt back to the pool or reject it to its capturer; approving approves every receipt left.' }}
            </li>
            <li>{{ 'ExpenseClaim_Next_4' | translateWF: 'Then Accounting reviews and posts it, and the cash is settled.' }}</li>
          </ol>
          <small class="block mt-3 text-surface-500">
            {{ 'ExpenseClaim_Next_NoPosting' | translateWF: 'Nothing posts until Accounting posts the claim.' }}
          </small>
        </p-card>
      </p-panel>

      <div class="grid-12 gap-[1.5rem]">
        <div class="span-12-12-8 flex flex-col gap-5">
          <p-panel [header]="'ExpenseClaim_Section_Data' | translateWF: 'Claim data'">
            <p-card>
              <form [formGroup]="form" class="grid grid-cols-12 gap-5">
                <div class="form-box col-span-12 md:col-span-6">
                  <label for="claim-owner">{{ fieldLabels.ownerKey | translateWF: 'Fund or trip' }} *</label>
                  @if (isNew()) {
                    <p-select
                      inputId="claim-owner"
                      formControlName="ownerKey"
                      [options]="ownerOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [filter]="true"
                      [placeholder]="'ExpenseClaim_Owner_Placeholder' | translateWF: 'Select a fund or trip'"
                      [emptyMessage]="'ExpenseClaim_Owner_Empty' | translateWF: 'You hold no active fund or open trip.'"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.ownerKey.touched && form.controls.ownerKey.invalid }"
                    />
                    <small class="text-surface-500">
                      {{ 'ExpenseClaim_Owner_Hint' | translateWF: 'A fund’s claims are prepared by its custodian; a trip’s by its employee or a TripAdmin.' }}
                    </small>
                  } @else {
                    @if (claim(); as current) {
                      <div class="flex items-center gap-2 py-2">
                        <i [class]="current.owner.entityDefinitionCode === ownerCodes.Trip ? 'pi pi-briefcase' : 'pi pi-wallet'" aria-hidden="true"></i>
                        <a class="cs-table-link" (click)="openOwner()">{{ current.ownerName }}</a>
                        <small class="text-surface-500 font-mono">{{ current.owner.number }}</small>
                      </div>
                    }
                  }
                </div>
                <div class="form-box col-span-12 md:col-span-6">
                  <label>{{ 'ExpenseClaim_Field_Policy' | translateWF: 'Approval policy' }}</label>
                  <div class="py-2">
                    @if (claim()?.claimPolicy; as policy) {
                      {{ policyName(policy.name) }}
                    } @else {
                      <span class="text-surface-500">{{ 'PettyCashFund_Policy_Default' | translateWF: 'Default claim policy' }}</span>
                    }
                  </div>
                </div>
                <div class="form-box col-span-12">
                  <label for="claim-description">{{ fieldLabels.description | translateWF: 'Description' }}</label>
                  <textarea
                    id="claim-description"
                    pTextarea
                    class="w-full"
                    rows="2"
                    maxlength="500"
                    formControlName="description"
                    [placeholder]="'ExpenseClaim_Description_Placeholder' | translateWF: 'e.g. October expenses, plant petty cash'"
                  ></textarea>
                </div>
              </form>
            </p-card>
          </p-panel>

          @if (reviewingReceipt(); as reviewing) {
            <p-panel>
              <ng-template #header>
                <span class="font-semibold">{{ 'ExpenseClaim_Reviewing' | translateWF: 'Reviewing receipt' }} {{ reviewing.number }}</span>
              </ng-template>
              <p-card>
                <app-expense-receipt-editor
                  [receipt]="reviewing"
                  [openKey]="reviewKey()"
                  [concepts]="concepts()"
                  mode="review"
                  (saved)="saveReview($event)"
                  (cancelled)="closeReview()"
                />
              </p-card>
            </p-panel>
          }

          @if (!isNew()) {
            <p-panel [header]="'ExpenseClaim_Section_Receipts' | translateWF: 'Receipts in this claim'">
              <p-card data-variant="compact">
                <p-table [value]="receipts()" dataKey="id" [rowHover]="true">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 13rem">{{ 'Receipt_Col_Number' | translateWF: 'Number' }}</th>
                      <th style="width: 7rem">{{ 'Receipt_Field_Date' | translateWF: 'Date' }}</th>
                      <th>{{ 'Receipt_Field_Vendor' | translateWF: 'Vendor' }}</th>
                      <th>{{ 'Receipt_Field_Concept' | translateWF: 'Concept' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'ExpenseClaim_Col_Total' | translateWF: 'Total' }}</th>
                      @if (canRemove()) {
                        <th style="width: 9rem"></th>
                      }
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-rowData>
                    @let row = getRow(rowData);
                    <tr>
                      <td class="font-mono">{{ row.number }}</td>
                      <td class="tabular-nums">{{ row.date | date: 'yyyy-MM-dd' : 'UTC' }}</td>
                      <td>{{ row.vendor?.name }}</td>
                      <td>{{ conceptsOf(row) }}</td>
                      <td class="text-right tabular-nums">
                        {{ symbolOf(row.currency) }} {{ row.total | number: '1.2-2' }}
                        @if (claimTotalOf(row); as converted) {
                          <small class="block text-surface-500">≈ {{ symbolOf(claim()?.currency) }} {{ converted | number: '1.2-2' }}</small>
                        }
                      </td>
                      @if (canRemove()) {
                        <td>
                          <div class="cs-table-actions">
                            @if (canReview()) {
                              <p-button
                                icon="pi pi-pencil"
                                [text]="true"
                                [rounded]="true"
                                [pTooltip]="'ExpenseClaim_ReviewReceipt' | translateWF: 'Review receipt'"
                                tooltipPosition="top"
                                (onClick)="openReview(row)"
                              />
                            }
                            <p-button
                              icon="pi pi-undo"
                              [text]="true"
                              [rounded]="true"
                              [pTooltip]="'ExpenseClaim_RemoveReceipt' | translateWF: 'Back to the pool'"
                              tooltipPosition="top"
                              (onClick)="removeReceipt(row)"
                            />
                            @if (canRejectReceipt()) {
                              <p-button
                                icon="pi pi-times"
                                severity="danger"
                                [text]="true"
                                [rounded]="true"
                                [pTooltip]="'ExpenseClaim_RejectReceipt' | translateWF: 'Reject receipt'"
                                tooltipPosition="top"
                                (onClick)="openRejectReceipt(row)"
                              />
                            }
                          </div>
                        </td>
                      }
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td [attr.colspan]="canRemove() ? 6 : 5" class="text-center">
                        {{ 'ExpenseClaim_Receipts_Empty' | translateWF: 'No receipts yet: add them from the pool below.' }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </p-card>
            </p-panel>
          }

          @if (showPool()) {
            <p-panel [header]="'ExpenseClaim_Section_Pool' | translateWF: 'Receipts in the pool'">
              <ng-template pTemplate="icons">
                @if (!isNew()) {
                  <p-button
                    [label]="'ExpenseClaim_AddSelected' | translateWF: 'Add selected'"
                    icon="pi pi-plus"
                    size="small"
                    [disabled]="selectedPool().length === 0"
                    (onClick)="addSelected()"
                  />
                }
              </ng-template>
              <p-card data-variant="compact">
                <p-table [value]="pool()" dataKey="id" [rowHover]="true" [selection]="selectedPool()" (selectionChange)="onPoolSelection($event)">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 3rem"><p-tableHeaderCheckbox /></th>
                      <th style="width: 13rem">{{ 'Receipt_Col_Number' | translateWF: 'Number' }}</th>
                      <th style="width: 7rem">{{ 'Receipt_Field_Date' | translateWF: 'Date' }}</th>
                      <th>{{ 'Receipt_Field_Vendor' | translateWF: 'Vendor' }}</th>
                      <th>{{ 'Receipt_Field_Concept' | translateWF: 'Concept' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'ExpenseClaim_Col_Total' | translateWF: 'Total' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-rowData>
                    @let row = getRow(rowData);
                    <tr>
                      <td><p-tableCheckbox [value]="row" /></td>
                      <td class="font-mono">{{ row.number }}</td>
                      <td class="tabular-nums">{{ row.date | date: 'yyyy-MM-dd' : 'UTC' }}</td>
                      <td>{{ row.vendor?.name }}</td>
                      <td>{{ conceptsOf(row) }}</td>
                      <td class="text-right tabular-nums">{{ symbolOf(row.currency) }} {{ row.total | number: '1.2-2' }}</td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="6" class="text-center">
                        {{ 'ExpenseClaim_Pool_Empty' | translateWF: 'No complete receipts in the pool. Capture them first on Capture receipts.' }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
                @if (selectedPool().length > 0) {
                  <small class="block mt-3 text-surface-500">
                    {{ selectedPool().length }} {{ 'ExpenseClaim_Selected' | translateWF: 'selected' }} · $ {{ selectedTotalUsd() | number: '1.2-2' }}
                    @if (isNew()) {
                      · {{ 'ExpenseClaim_Selected_OnCreate' | translateWF: 'they join the claim when you create it' }}
                    }
                  </small>
                }
              </p-card>
            </p-panel>
          }
        </div>

        <div class="span-12-12-4 flex flex-col gap-5 sticky-summary">
          @if (claim()?.approval; as approval) {
            <p-panel [header]="'ExpenseClaim_Section_Approval' | translateWF: 'Approval'">
              <p-card>
                <div class="flex flex-col gap-3">
                  <span class="text-sm text-surface-500">
                    {{ 'Trip_Approval_Policy' | translateWF: 'Policy' }}: {{ policyName(approval.policyName) }} · $
                    {{ approval.evaluatedUsdAmount | number: '1.2-2' }}
                  </span>
                  @if (approval.isAutoApproved) {
                    <p-message severity="info" [text]="'Trip_Approval_Auto' | translateWF: 'No approver applied: approved by the system.'" />
                  }
                  @for (view of slotViews(); track view.slot.id) {
                    <div class="flex items-start justify-between gap-3 py-1">
                      <span class="flex items-center gap-2">
                        <i [class]="slotIcon(view.slot)" aria-hidden="true"></i>
                        <span class="flex flex-col">
                          <span>{{ view.slot.value }}</span>
                          @if (view.by) {
                            <small class="text-surface-500">{{ view.by }} · {{ view.at | date: 'yyyy-MM-dd HH:mm' }}</small>
                          }
                          @if (view.comment) {
                            <small class="text-surface-500 italic">“{{ view.comment }}”</small>
                          }
                        </span>
                      </span>
                      @switch (view.state) {
                        @case ('approved') {
                          <p-tag severity="success" [value]="'Trip_Slot_Approved' | translateWF: 'Approved'" />
                        }
                        @case ('rejected') {
                          <p-tag severity="danger" [value]="'Trip_Slot_Rejected' | translateWF: 'Rejected'" />
                        }
                        @default {
                          <p-tag
                            severity="secondary"
                            [value]="view.slot.isRequired ? ('Trip_Slot_Pending' | translateWF: 'Pending') : ('Trip_Slot_Optional' | translateWF: 'Optional')"
                          />
                        }
                      }
                    </div>
                  }
                </div>
              </p-card>
            </p-panel>
          }
          @if (claim(); as current) {
            <app-settlement-summary
              [title]="'ExpenseClaim_Section_Summary' | translateWF: 'Claim summary'"
              [hint]="summaryHint()"
              [baseLabel]="'Receipt_Subtotal' | translateWF: 'Subtotal before ISV'"
              [baseAmount]="summary().subtotal"
              [extraRows]="summaryRows()"
              [netLabel]="summaryNetLabel()"
              [net]="current.total"
              [currencyLabel]="currencyCode()"
            />
          }

          @if (showPreview()) {
            @if (preview(); as entry) {
              <p-panel [header]="'ExpenseClaim_Section_Journal' | translateWF: 'Journal entry to post'">
                <p-card data-variant="compact">
                  @if (entry.missing.length > 0) {
                    <p-message severity="warn" styleClass="mb-2">
                      {{ 'ExpenseClaim_Journal_Missing' | translateWF: 'These receipt lines have no GL account; review them before posting:' }}
                      {{ entry.missing.join(', ') }}
                    </p-message>
                  }
                  <p-table [value]="entry.lines" [rowHover]="true">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'ExpenseClaim_Journal_Account' | translateWF: 'Account' }}</th>
                        <th class="text-right" style="width: 7rem">{{ 'ExpenseClaim_Journal_Debit' | translateWF: 'Debit' }}</th>
                        <th class="text-right" style="width: 7rem">{{ 'ExpenseClaim_Journal_Credit' | translateWF: 'Credit' }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-rowData>
                      @let line = getJournalRow(rowData);
                      <tr>
                        <td>
                          <span class="font-mono">{{ line.accountNumber }}</span>
                          <small class="block">{{ line.accountName }}</small>
                          <small class="block text-surface-500">
                            {{ line.description }}
                            @if (line.costCenter) {
                              · {{ line.costCenter }}
                            }
                          </small>
                        </td>
                        <td class="text-right tabular-nums">{{ line.debit ? (line.debit | number: '1.2-2') : '' }}</td>
                        <td class="text-right tabular-nums">{{ line.credit ? (line.credit | number: '1.2-2') : '' }}</td>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="footer">
                      <tr>
                        <td class="text-right font-semibold">{{ 'ExpenseClaim_Journal_Totals' | translateWF: 'Totals' }} ({{ symbolOf(entry.currency) }})</td>
                        <td class="text-right tabular-nums font-semibold">{{ entry.totalDebit | number: '1.2-2' }}</td>
                        <td class="text-right tabular-nums font-semibold">{{ entry.totalCredit | number: '1.2-2' }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            }
          }

          @if (isPosted()) {
            <p-panel [header]="'ExpenseClaim_Section_Posted' | translateWF: 'Posted'">
              <p-card>
                <small class="block mb-3 text-surface-500"> {{ 'ExpenseClaim_PostedOn' | translateWF: 'Posted on' }} {{ claim()?.postingDate | date: 'yyyy-MM-dd' : 'UTC' }} </small>
                @if (claim(); as posted) {
                  <div class="flex flex-col gap-1 mb-3 text-sm">
                    <span>{{ 'ExpenseClaim_Settled' | translateWF: 'Settled' }}: {{ symbolOf(posted.currency) }} {{ posted.settledAmount | number: '1.2-2' }}</span>
                    <span class="font-semibold"
                      >{{ 'ExpenseClaim_Outstanding' | translateWF: 'Still to settle' }}: {{ symbolOf(posted.currency) }} {{ posted.outstanding | number: '1.2-2' }}</span
                    >
                  </div>
                }
                <app-journal-entry-summary [references]="claim()?.journalEntryReference" [currencyLabel]="currencyCode()" />
              </p-card>
            </p-panel>
          }
        </div>
      </div>
    }

    <p-dialog
      [visible]="decisionVisible()"
      (visibleChange)="decisionVisible.set($event)"
      [modal]="true"
      [style]="{ width: '32rem' }"
      [header]="
        decisionMode() === 'approve'
          ? ('ExpenseClaim_Approve_Title' | translateWF: 'Approve expense claim')
          : decisionMode() === 'reject'
            ? ('ExpenseClaim_Reject_Title' | translateWF: 'Reject expense claim')
            : decisionMode() === 'sendBack'
              ? ('ExpenseClaim_SendBack_Title' | translateWF: 'Send back to approval')
              : ('ExpenseClaim_RejectReceipt_Title' | translateWF: 'Reject receipt') + ' ' + (decisionReceipt()?.number ?? '')
      "
    >
      <div class="form-box">
        <label for="claim-decision-comment">
          {{ 'Trip_Decision_Comment' | translateWF: 'Comment' }}
          @if (isRejection()) {
            *
          }
        </label>
        <textarea
          id="claim-decision-comment"
          pTextarea
          class="w-full"
          rows="3"
          maxlength="500"
          [formControl]="decisionComment"
          [ngClass]="{ 'ng-invalid ng-dirty': isRejection() && decisionComment.touched && !decisionComment.value.trim() }"
        ></textarea>
        <small class="text-surface-500">
          @switch (decisionMode()) {
            @case ('approve') {
              {{ 'ExpenseClaim_Approve_Hint' | translateWF: 'Approves every receipt still in the claim.' }}
            }
            @case ('reject') {
              {{ 'ExpenseClaim_Reject_Hint' | translateWF: 'Say why; the claim goes back to Draft with its receipts.' }}
            }
            @case ('sendBack') {
              {{ 'ExpenseClaim_SendBack_Hint' | translateWF: 'Say why; the approvers are resolved again and decide the claim once more.' }}
            }
            @default {
              {{ 'ExpenseClaim_RejectReceipt_Hint' | translateWF: 'Say why; the receipt leaves the claim and goes back to its capturer to correct.' }}
            }
          }
        </small>
      </div>
      <ng-template #footer>
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="decisionVisible.set(false)" />
        <p-button
          [label]="
            decisionMode() === 'sendBack'
              ? ('ExpenseClaim_SendBack' | translateWF: 'Back to approval')
              : isRejection()
                ? ('ExpenseClaim_Reject' | translateWF: 'Reject')
                : ('ExpenseClaim_Approve' | translateWF: 'Approve')
          "
          [severity]="isRejection() ? 'danger' : 'success'"
          (onClick)="confirmDecision()"
        />
      </ng-template>
    </p-dialog>

    <p-dialog
      [visible]="postVisible()"
      (visibleChange)="postVisible.set($event)"
      [modal]="true"
      [style]="{ width: '30rem' }"
      [header]="'ExpenseClaim_Post_Title' | translateWF: 'Post expense claim'"
    >
      <div class="form-box">
        <label for="claim-posting-date">{{ 'ExpenseClaim_PostingDate' | translateWF: 'Posting date' }} *</label>
        <p-datepicker
          inputId="claim-posting-date"
          [formControl]="postingDate"
          [showIcon]="true"
          appendTo="body"
          dateFormat="yy-mm-dd"
          styleClass="w-full"
          [ngClass]="{ 'ng-invalid ng-dirty': postingDate.touched && postingDate.invalid }"
        />
        <small class="text-surface-500">
          {{ 'ExpenseClaim_Post_Hint' | translateWF: 'Posts one journal entry for the whole claim (Petty Cash module); its period must be open. The receipts become Posted.' }}
        </small>
      </div>
      <ng-template #footer>
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="postVisible.set(false)" />
        <p-button [label]="'ExpenseClaim_Post' | translateWF: 'Post'" icon="pi pi-book" severity="success" (onClick)="confirmPost()" />
      </ng-template>
    </p-dialog>
  </div>
</main>
